import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Manages a CSS opacity fade-in/fade-out lifecycle on a referenced element.
 *
 * - When `visible` becomes true, fades the element in (opacity 0 → 1).
 * - When `visible` becomes false, fades the element out (opacity 1 → 0).
 * - Applies transition and opacity styles directly to the referenced element.
 * - Uses a callback ref to reliably detect when the element mounts.
 *
 * Returns:
 * - `isFadingOut`: true while the fade-out transition is running
 * - `fadeRef`: callback ref to attach to the element
 */
export const useFadeTransition = ({ visible, durationMs }: {
    visible: boolean;
    durationMs: number;
}) => {
    const [fadingOut, setFadingOut] = useState(false);
    const fadeOutCompleteRef = useRef(false);
    const elementRef = useRef<HTMLElement | null>(null);

    // Track whether the element has been rendered, so isFadingOut can be true
    // during the single-render gap between visible going false and the
    // fade-out effect running.
    const wasRenderedRef = useRef(false);
    if (visible) {
        wasRenderedRef.current = true;
    }
    if (!visible && !fadingOut && fadeOutCompleteRef.current) {
        wasRenderedRef.current = false;
    }

    const completeFadeOut = useCallback(() => {
        if (fadeOutCompleteRef.current) {
            return;
        }
        fadeOutCompleteRef.current = true;
        setFadingOut(false);
    }, []);

    // Callback ref — called when the element mounts.
    // Sets up initial styles and triggers fade-in.
    const fadeRef = useCallback((el: HTMLElement | null) => {
        elementRef.current = el;
        if (el && visible) {
            el.style.transition = `opacity ${durationMs}ms ease-out`;
            el.style.opacity = '0';
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    el.style.opacity = '1';
                });
            });
        }
    }, [visible, durationMs]);

    // Start fade-out when visible becomes false while element is mounted
    useEffect(() => {
        if (!visible && elementRef.current) {
            fadeOutCompleteRef.current = false;
            setFadingOut(true);
            elementRef.current.style.opacity = '0';
        }
    }, [visible]);

    // Listen for transitionend and set up timeout fallback
    useEffect(() => {
        if (!fadingOut) {
            return;
        }

        const el = elementRef.current;
        const handleTransitionEnd = () => completeFadeOut();

        if (el) {
            el.addEventListener('transitionend', handleTransitionEnd);
        }
        const timeout = setTimeout(completeFadeOut, durationMs + 100);

        return () => {
            if (el) {
                el.removeEventListener('transitionend', handleTransitionEnd);
            }
            clearTimeout(timeout);
        };
    }, [fadingOut, durationMs, completeFadeOut]);

    // isFadingOut includes the gap between visible going false and the
    // fade-out effect running, so the consumer's element stays mounted.
    const isFadingOut = fadingOut || (!visible && wasRenderedRef.current);

    return { isFadingOut, fadeRef };
};
